import type { ComponentType } from "react";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { useFormStore } from "@/widgets/classic-form/form";
import { GroupRemoveItem } from "@/features/fields-grid";
import type { FieldMapperProps } from "@/features/fields-grid";
import { getEventInstancePath } from "@/entities/data-model";
import type { EventFieldset } from "@/entities/form-metadata";
import { EventAssertion, FieldType } from "@/entities/form-metadata";
import { Grid } from "@/shared/components/Grid";
import { Show } from "@/shared/components/Show";
import type { YearRangeGridConfig } from "../gridLayout";
import styles from "./EventInstanceRow.module.scss";

interface EventInstanceRowProps {
    instanceIndex: number;
    fieldset: EventFieldset;
    /** Identifies the row (remove-button test id). Cells bind through their own field's path. */
    fieldPath: string;
    onRemove: () => void;
    FieldMapper: ComponentType<FieldMapperProps<EventAssertion>>;
    gridConfig: YearRangeGridConfig;
}

export const EventInstanceRow: React.FC<EventInstanceRowProps> = ({
    fieldset,
    instanceIndex,
    fieldPath,
    onRemove,
    FieldMapper,
    gridConfig,
}) => {
    const { t } = useTranslation();
    // `isRangeMode` is written under the YearRange field's own path, so it must be read from there too.
    const yearRangeField = fieldset.fields.find((field) => field.fieldType === FieldType.YearRange);
    const rangeModePath = yearRangeField && `${getEventInstancePath(yearRangeField, instanceIndex)}.isRangeMode`;
    const isRangeMode = useFormStore(({ values }) => Boolean(rangeModePath && get(values, rangeModePath)));

    const getRemovable = () => (
        <span className={styles.removable}>
            <GroupRemoveItem
                ariaLabel={t("removeInstance", {
                    instanceName: fieldset.label || "",
                    instanceIndex: instanceIndex + 1,
                })}
                testId={`removeInstance-${fieldPath}`}
                removeItem={onRemove}
            />
        </span>
    );

    return (
        <fieldset>
            <Grid.Container className={styles.gridRow}>
                <Grid.Field spanDesktop={2} spanTablet={12} spanMobile={12}>
                    {/* Desktop Breakpoint */}
                    <Show.Above breakpoint={700}>
                        <legend className={styles.desktopLegend}>{fieldset.label}</legend>
                    </Show.Above>

                    {/* Mobile Breakpoint */}
                    <Show.Below breakpoint={700}>
                        <Grid.Container>
                            <Grid.Field spanMobile={11} spanDesktop={11} spanTablet={11}>
                                <div className={styles.legendContainer}>
                                    <legend className={styles.mobileLegend}>{fieldset.label}</legend>
                                </div>
                            </Grid.Field>
                            {gridConfig.Removable && (
                                <Grid.Field spanMobile={1} spanTablet={1} spanDesktop={1}>
                                    {getRemovable()}
                                </Grid.Field>
                            )}
                        </Grid.Container>
                    </Show.Below>
                </Grid.Field>

                <Grid.Field spanMobile={12} spanDesktop={9} spanTablet={12}>
                    <Grid.Container>
                        {fieldset.fields.map((field) => {
                            // Hide Day and Month fields only when the user is in range mode
                            const isDayOrMonth =
                                field.assertion === EventAssertion.Day || field.assertion === EventAssertion.Month;
                            if (isRangeMode && isDayOrMonth) {
                                return null;
                            }

                            const gridKey = field.assertion;
                            // Each field binds to its own state entry, not the fieldset's collapsed identity.
                            const perFieldPath = getEventInstancePath(field, instanceIndex);
                            return (
                                <Grid.Field
                                    key={field.assertion}
                                    spanDesktop={gridConfig.Assertions.desktop[gridKey]}
                                    spanTablet={gridConfig.Assertions.tablet[gridKey]}
                                    spanMobile={gridConfig.Assertions.mobile[gridKey]}
                                >
                                    {field.fieldType !== FieldType.YearRange && (
                                        <span className={styles.mobileFieldLabel} aria-hidden="true">
                                            {field.label}
                                        </span>
                                    )}
                                    <FieldMapper
                                        fieldData={field}
                                        fieldPath={perFieldPath}
                                        assertion={field.assertion}
                                        fieldsetLabel={fieldset.label}
                                        relation={field.relation}
                                        eventType={field.eventType}
                                        inputProperties={{
                                            "aria-label": `${fieldset.label ?? ""} ${field.label ?? ""}`,
                                        }}
                                    />
                                </Grid.Field>
                            );
                        })}
                    </Grid.Container>
                </Grid.Field>

                {gridConfig.Removable && (
                    <Show.Above breakpoint={700}>
                        <Grid.Field spanMobile={2} spanTablet={2} spanDesktop={2}>
                            {getRemovable()}
                        </Grid.Field>
                    </Show.Above>
                )}
            </Grid.Container>
        </fieldset>
    );
};
